import { DataTable } from "@/components/ui/data-table";
import React from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddChallengeDialog from "@/pages/dashboard/nutrition/addChallenge";
import { axiosPrivate } from "@/api/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { toast } from "sonner";
import ChallengeEditDialog, { ChallengeEditForm } from "@/pages/dashboard/nutrition/ChallengeEditDialog";
import { formatDateTimeGMT5 } from "@/utils/timezone";

function NutritionDailyChallenges() {
  // Placeholder columns and data for daily challenges
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  // Edit dialog state
  const [openEdit, setOpenEdit] = React.useState(false);
  const [selectedChallenge, setSelectedChallenge] = React.useState<any | null>(null);
  const [form, setForm] = React.useState<ChallengeEditForm>({
    title: "",
    description: "",
    type: "nutrition",
    duration: 0,
    duration_type: "minute",
    reward_time: 0,
    reward_time_type: "minute",
  });

  // Confirm delete state
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});
  // Use GMT+5 timezone for date formatting
  const formatDateTime = React.useCallback((input: string) => {
    if (!input) return "";
    const d = new Date(input);
    if (isNaN(d.getTime())) return String(input);
    return formatDateTimeGMT5(d);
  }, []);
  const { mutate: updateChallenge, isPending: saving } = useMutation({
    mutationFn: async (payload: any) => {
      const { id, ...rest } = payload;
      await axiosPrivate.put(`/challenges/${id}`, rest);
    },
    onSuccess: () => {
      toast.success("Challenge updated");
      queryClient.invalidateQueries({ queryKey: ["challenges", "nutrition"], exact: false });
      setOpenEdit(false);
    },
    onError: () => toast.error("Failed to update challenge"),
  });

  const { mutate: deleteChallenge, isPending: isDeleting } = useMutation({
    mutationFn: async (id: number) => {
      await axiosPrivate.delete(`/challenges/${id}`);
    },
    onSuccess: () => {
      toast.success("Challenge deleted");
      queryClient.invalidateQueries({ queryKey: ["challenges", "nutrition"], exact: false });
    },
    onError: (err: any) => {
      const raw = err?.response?.data?.message || err?.message || "";
      const msg = String(raw);
      if (/user_challenges|NotNullViolation/i.test(msg)) {
        toast.error(
          `Cannot delete "${selectedChallenge?.title || "challenge"}": it's referenced by user progress. Remove dependent user-challenges or ask backend to enable cascade/soft delete.`
        );
      } else {
        toast.error("Failed to delete challenge");
      }
    },
  });

  function handleOpenEdit(challenge: any) {
    setSelectedChallenge(challenge);
    setForm({
      title: challenge.title || "",
      description: challenge.description || "",
      type: (challenge.type as "gut" | "nutrition" | "sleep") || "gut",
      duration: Number(challenge.duration) || 0,
      duration_type: (challenge.duration_type as "minute" | "hour" | "day") || "minute",
      reward_time: Number(challenge.reward_time) || 0,
      reward_time_type: (challenge.reward_time_type as "minute" | "hour" | "day") || "minute",
    });
    setOpenEdit(true);
  }

  const columns = [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "title", header: "Challenge Title" },
    { 
      accessorKey: "description", 
      header: "Description",
      cell: ({ row }: { row: any }) => {
        const text = row.getValue("description") as string;
        return (
          <span className="truncate max-w-[32ch] inline-block" title={text}>
            {text}
          </span>
        );
      },
    },
    { accessorKey: "type", header: "Type" },
    { accessorKey: "duration_display", header: "Duration" },
    { accessorKey: "reward_time_display", header: "Reward Time" },
    { accessorKey: "created_at", header: "Created At" },
    {
      accessorKey: "action",
      header: "Action",
      cell: ({ row }: { row: any }) => {
        const challenge = row.original;
        return (
          <ActionsMenu
            onEdit={() => handleOpenEdit(challenge)}
            onDeleteConfirm={() => {
              // Defer to allow dropdown to close
              setTimeout(() => {
                setSelectedChallenge(challenge);
                confirmActionButtonRef.current = () => {
                  setOpenConfirm(false);
                  deleteChallenge(challenge.id);
                };
                setOpenConfirm(true);
              }, 0);
            }}
          />
        );
      },
    },
  ];
  // Fetch challenges
  const { data: rawChallenges, isLoading } = useQuery({
    queryKey: ["challenges", "nutrition"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/challenges/type/nutrition");
      const payload = Array.isArray(res.data?.data) ? res.data.data : res.data;
      return Array.isArray(payload) ? payload : [];
    },
    placeholderData: [],
  });

  const data: Array<{ 
    id: number; 
    title: string; 
    description: string; 
    type: string;
    duration?: number;
    duration_type?: "minute" | "hour" | "day" | string;
    reward_time?: number;
    reward_time_type?: "minute" | "hour" | "day" | string;
    duration_display: string;
    reward_time_display: string;
    created_at: string;
  }> = React.useMemo(() => {
    const list = rawChallenges || [];
    return list.map((c: any) => ({
      id: Number(c.id ?? 0),
      title: String(c.title ?? ""),
      description: String(c.description ?? ""),
      type: String(c.type ?? ""),
      duration: c.duration != null ? Number(c.duration) : undefined,
      duration_type: c.duration_type ?? undefined,
      reward_time: c.reward_time != null ? Number(c.reward_time) : undefined,
      reward_time_type: c.reward_time_type ?? undefined,
      duration_display: `${c.duration ?? 0} ${c.duration_type ?? ""}`.trim(),
      reward_time_display: `${c.reward_time ?? 0} ${c.reward_time_type ?? ""}`.trim(),
      created_at: formatDateTime(String(c.created_at ?? c.createdAt ?? "")),
    }));
  }, [rawChallenges, formatDateTime]);
  
  
  return (
    <div className="space-y-4">
  <h1 className="text-3xl">Daily Challenges Management</h1>
  <p>Manage all the daily nutrition challenges in one place.</p>
      <div className="flex justify-start">
        <Button size="sm" onClick={() => setOpen(true)} className="flex items-center gap-2">
          <Plus size={16} /> Add Challenge
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          placeholder="Filter Challenges"
          filterColumns={["title", "description", "type"]}
        />
      )}
      {/* Edit & Confirm Dialogs */}
      <ChallengeEditDialog
        open={openEdit}
        onOpenChange={setOpenEdit}
        form={form}
        setForm={setForm}
  onSave={(values: ChallengeEditForm) => {
          if (!selectedChallenge) return;
          updateChallenge({ id: selectedChallenge.id, ...values });
        }}
      />
      <ConfirmDialog
        actionButtonRef={confirmActionButtonRef}
        open={openConfirm}
        onOpenChange={setOpenConfirm}
        isLoading={isDeleting}
        AlertdialogDescription="This will permanently delete this challenge. Are you sure?"
      />
      {/* Create dialog */}
  <AddChallengeDialog
        open={open}
        onOpenChange={setOpen}
        section="nutrition"
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["challenges", "nutrition"], exact: false })}
      />
    </div>
  );
}

export default NutritionDailyChallenges;
